export interface IngredientOption {
  id: string;
  name: string;
  price: number;
  isSoldOut: boolean;
}

export interface ItemVariant {
  id: string;
  name: string;
  price: number;
  comboPrice: number;
  isSoldOut: boolean;
}

export interface BaseItemVariants {
  id: string;
  name: string;
  description: string;
  variants: ItemVariant[];
}

export interface BaseItemOptions {
  id: string;
  name: string;
  description: string;
  options: IngredientOption[];
}

export interface BaseItem {
  id: string;
  name: string;
  price: number;
  imageUrl?: string;
  includedInMetrics: boolean;
  isSoldOut: boolean;
  variants: BaseItemVariants[];
  options: BaseItemOptions[];
}

export interface ComboVariant {
  id: string;
  name: string;
  price: number;
  imageUrl?: string;
  itemVariantIds: string[];
}

export interface MenuItem {
  id: string;
  baseItemId: string;
  isOnSale: boolean;
  salePrice?: number;
  isCombo: boolean;
  comboVariants?: ComboVariant[];
  imageUrl?: string;
}

export interface MenuCategory {
  id: string;
  name: string;
  items: MenuItem[];
}

export interface VariantPriceOverride {
  id: string;
  variantId: string;
  price: number;
}

export interface BundleItem {
  id: string;
  menuItemId: string;
  quantity: number;
  noVariantPriceOverride?: number;
  variantPriceOverrides: VariantPriceOverride[];
}

export interface Bundle {
  id: string;
  name: string;
  description?: string;
  items: BundleItem[];
  imageUrl?: string;
}

export interface FullMenuPageData {
  baseItems: BaseItem[];
  menu: {
    categories: MenuCategory[];
    bundles: Bundle[];
  };
}


export interface bundleViewData {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  items: {
    id: string;
    menuItem: MenuItem;
    quantity: number;
    noVariantPriceOverride?: number;
    variantPriceOverrides: VariantPriceOverride[];
  }[];
}

export function decorateBundle(bundle: Bundle, menuData: FullMenuPageData): bundleViewData {
  let items: {
    id: string;
    menuItem: MenuItem;
    quantity: number;
    noVariantPriceOverride?: number;
    variantPriceOverrides: VariantPriceOverride[];
  }[] = [];
  
  bundle.items.forEach(item => {
    menuData.menu.categories.forEach(cat => {
      cat.items.forEach(menuItem => {
        if (item.menuItemId === menuItem.id) {
          items.push({
            id: item.id,
            quantity: item.quantity,
            variantPriceOverrides: item.variantPriceOverrides,
            noVariantPriceOverride: item.noVariantPriceOverride,
            menuItem: menuItem
          })
        }
      })
    })
  })
  return {
    ...bundle,
    items: items
  }
}


/**
 * Helper: find baseItem & variant and group given an id which may be either:
 * - a baseItem.id (then variantId is undefined), or
 * - a variant.id (leaf) — then we return the parent baseItem and the variant + group id
 */
export function findBaseItemAndVariant(menuData: FullMenuPageData, id: string): {
  baseItem?: BaseItem;
  variant?: ItemVariant;
  groupId?: string;
} {
  // Try baseItem id first
  const byBase = menuData.baseItems.find(b => b.id === id);
  if (byBase) return { baseItem: byBase };

  // Search groups/variants
  for (const b of menuData.baseItems) {
    for (const group of b.variants) {
      for (const v of group.variants) {
        if (v.id === id) return { baseItem: b, variant: v, groupId: group.id };
      }
    }
  }
  return {};
}